// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    IVmResponse,
    VZNetfilterStatus,
} from 'common/api/resources/ComputeResourceVm';
import {
    ImageFormat,
    StorageType,
} from 'common/api/resources/StorageType';
import {
    IPaginated,
    IRequest,
    ISearchable,
} from 'common/api/resources/Request/request';
import {
    BandwidthUnit,
    convertToDataUnit,
    DataUnit,
    DiskBandwidthUnit,
    IOpsUnit,
    Unit,
    Units,
} from 'common/helpers/units';
import { VirtualizationType } from 'common/api/resources/ComputeResource';
import { IShortLocationResponse } from 'common/api/resources/Location';
import { IShortApplicationResponse } from 'common/api/resources/Application';
import { IShortOsImageVersionResponse } from 'common/api/resources/OsImageVersion';
import { IShortImagePresetResponse } from 'common/api/resources/ImagePreset';
import { IVGpuProfile } from 'common/api/resources/Gpu';

interface IPlanParams {
    disk: number;
    ram: number;
    vcpu: number;
    vcpu_units?: number;
    vcpu_limit?: number;
    io_priority?: number;
    swap?: number;
}

export interface IPlanLimit<T = Units> {
    limit: number;
    unit: T;
    is_enabled: boolean;
}

export function limitFactory<T>(unit: T, limit: number = 1, is_enabled: boolean = false): IPlanLimit<T> {
    return {
        unit,
        limit,
        is_enabled,
    };
}

export const areNetworkLimitsExceeded = (server: IVmResponse) => server.usage.network.incoming.is_exceeded || server.usage.network.outgoing.is_exceeded;
export const isLimitExceeded = (currentUsage: number, limit: IPlanLimit<DataUnit>): boolean =>
    limit.is_enabled && convertToDataUnit(currentUsage, limit.unit) >= limit.limit;

export enum ResetLimitPolicy {
    Never = 'never',
    FirstDayOfMonth = 'first_day_of_month',
    VmCreatedDay = 'vm_created_day',
}

export enum NetworkTrafficLimitTypes {
    Separate = 'separate',
    Total = 'total',
}

export enum LimitName {
    DiskIops = 'disk_iops',
    DiskBandwidth = 'disk_bandwidth',
    NetworkIncomingBandwidth = 'network_incoming_bandwidth',
    NetworkOutgoingBandwidth = 'network_outgoing_bandwidth',
    NetworkIncomingTraffic = 'network_incoming_traffic',
    NetworkOutgoingTraffic = 'network_outgoing_traffic',
    NetworkTotalTraffic = 'network_total_traffic',
    NetworkReduceBandwidth = 'network_reduce_bandwidth',
    BackupsNumber = 'backups_number',
    SnapshotsCount = 'snapshots_count',
    SnapshotsSize = 'snapshots_size',
    AdditionalDiskNumber = 'additional_disks_number',
}

export interface IPlanLimits {
    [LimitName.DiskIops]: IPlanLimit<IOpsUnit>;
    [LimitName.DiskBandwidth]: IPlanLimit<DiskBandwidthUnit>;
    [LimitName.NetworkIncomingBandwidth]: IPlanLimit<BandwidthUnit>;
    [LimitName.NetworkOutgoingBandwidth]: IPlanLimit<BandwidthUnit>;
    [LimitName.NetworkIncomingTraffic]: IPlanLimit<DataUnit>;
    [LimitName.NetworkOutgoingTraffic]: IPlanLimit<DataUnit>;
    [LimitName.NetworkTotalTraffic]: IPlanLimit<DataUnit>;
    [LimitName.NetworkReduceBandwidth]: IPlanLimit<BandwidthUnit>;
    [LimitName.BackupsNumber]: IPlanLimit<Unit>;
    [LimitName.SnapshotsCount]: IPlanLimit<Unit>;
    [LimitName.SnapshotsSize]: IPlanLimit<DataUnit>;
    [LimitName.AdditionalDiskNumber]: IPlanLimit<Unit>;
}

export interface IPlanUpdateLimits {
    [LimitName.NetworkIncomingBandwidth]: IPlanLimit<BandwidthUnit>;
    [LimitName.NetworkOutgoingBandwidth]: IPlanLimit<BandwidthUnit>;
    [LimitName.NetworkIncomingTraffic]: IPlanLimit<DataUnit>;
    [LimitName.NetworkOutgoingTraffic]: IPlanLimit<DataUnit>;
    [LimitName.NetworkTotalTraffic]: IPlanLimit<DataUnit>;
    [LimitName.NetworkReduceBandwidth]: IPlanLimit<BandwidthUnit>;
    [LimitName.BackupsNumber]: IPlanLimit<Unit>;
    [LimitName.SnapshotsCount]: IPlanLimit<Unit>;
    [LimitName.SnapshotsSize]: IPlanLimit<DataUnit>;
    [LimitName.AdditionalDiskNumber]: IPlanLimit<Unit>;
}

export interface IPlanCreateRequest {
    name: string;
    params: IPlanParams;
    virtualization_type: VirtualizationType;
    storage_type: StorageType;
    image_format: ImageFormat;
    tokens_per_hour: number;
    tokens_per_month: number;
    is_backup_available: boolean;
    backup_price: number;
    is_visible: boolean;
    is_snapshots_enabled: boolean;
    limits: IPlanLimits;
    is_additional_ips_available: boolean;
    ip_tokens_per_hour: number;
    ip_tokens_per_month: number;
    iso_image_tokens_per_hour: number;
    iso_image_tokens_per_month: number;
    reset_limit_policy: ResetLimitPolicy;
    network_traffic_limit_type: NetworkTrafficLimitTypes;
    backup_settings: IPlanBackupSettings;
    available_locations: number[];
    available_os_image_versions: number[];
    available_applications: number[];
    is_high_availability_cr_enabled: boolean;
    is_disaster_recovery_enabled: boolean;
    disaster_recovery_price: number;
    high_availability_cr_price: number;
    image_preset_ids?: number[];
    gpu_profile_id?: number | null;
    netfilter?: {
        value: VZNetfilterStatus;
        is_editable: boolean;
    };
    tun_tap?: {
        value: boolean;
        is_editable: boolean;
    };
    ppp?: {
        value: boolean;
        is_editable: boolean;
    };
}

export type PlanUpdateRequest = IKVMPlanUpdateRequest | IVZPlanUpdateRequest;

export interface IKVMPlanUpdateRequest extends ICommonPlanUpdateRequest {}

export interface IVZPlanUpdateRequest extends ICommonPlanUpdateRequest {
    netfilter: {
        value: VZNetfilterStatus;
        is_editable: boolean;
    };
    tun_tap: {
        value: boolean;
        is_editable: boolean;
    };
    ppp: {
        value: boolean;
        is_editable: boolean;
    };
}

interface ICommonPlanUpdateRequest {
    name: string;
    tokens_per_hour: number;
    tokens_per_month: number;
    is_backup_available: boolean;
    is_snapshots_enabled: boolean;
    is_visible: boolean;
    backup_price: number;
    is_additional_ips_available: boolean;
    ip_tokens_per_month: number;
    ip_tokens_per_hour: number;
    iso_image_tokens_per_hour: number;
    iso_image_tokens_per_month: number;
    limits: IPlanUpdateLimits;
    network_traffic_limit_type: NetworkTrafficLimitTypes;
    backup_settings: IPlanBackupSettings;
    available_locations: number[];
    available_os_image_versions: number[];
    available_applications: number[];
    is_high_availability_cr_enabled: boolean;
    is_disaster_recovery_enabled: boolean;
    disaster_recovery_price: number;
    high_availability_cr_price: number;
}

export interface IPlanPatchRequest {
    is_default?: boolean;
    is_visible?: boolean;
    position?: number;
}

export interface IPrice {
    currency_code: string;
    per_hour: string;
    per_month: string;
    taxes_inclusive: boolean;
    taxes: ITax[];
    total_price: string;
    total_price_without_backups: string;
    backup_price: string;
    additional_ips_per_month: string;
    additional_ips_per_hour: string;
}

export interface ITax {
    label: string;
    rate: number;
}

export interface IPlanBackupSettings {
    incremental_backups_limit: number;
    is_incremental_backup_enabled: boolean;
}

export interface IPlanResponse {
    id: number;
    name: string;
    virtualization_type: VirtualizationType;
    params: IPlanParams;
    storage_type: StorageType;
    image_format: ImageFormat;
    reset_limit_policy: ResetLimitPolicy;
    is_snapshot_available: boolean;
    is_snapshots_enabled: boolean;
    is_default: boolean;
    is_loading: boolean;
    is_deleting: boolean;
    limits: IPlanLimits;
    is_backup_available: boolean;
    backup_settings: IPlanBackupSettings;
    is_visible: boolean;
    is_thin_provisioned: boolean;
    is_custom: boolean;
    position: number;
    is_additional_ips_available: boolean;
    network_traffic_limit_type: NetworkTrafficLimitTypes;
    available_locations?: IShortLocationResponse[];
    available_os_image_versions?: IShortOsImageVersionResponse[];
    available_applications?: IShortApplicationResponse[];
    image_presets: IShortImagePresetResponse[];
    netfilter?: {
        value: VZNetfilterStatus;
        is_editable: boolean;
    };
    tun_tap?: {
        value: boolean;
        is_editable: boolean;
    };
    ppp?: {
        value: boolean;
        is_editable: boolean;
    };
    tokens_per_hour: number;
    tokens_per_month: number;
    backup_price: number;
    ip_tokens_per_month: number;
    ip_tokens_per_hour: number;
    iso_image_tokens_per_hour: number;
    iso_image_tokens_per_month: number;
    is_disaster_recovery_enabled: boolean;
    disaster_recovery_price: number;
    is_high_availability_cr_enabled: boolean;
    high_availability_cr_price: number;
    gpu_profile?: IVGpuProfile | null;
}

export interface IShortPlanResponse {
    id: number;
    name: string;
}

export interface IPlanListFilters {
    storage_type?: StorageType;
    image_format?: ImageFormat;
    disk?: string[];
    search?: string;
    location_id?: number;
    virtualization_type?: VirtualizationType;
}

export interface IPlanListAdditionalParameters {
    additional_ips_count?: number;
}

export type PlanListAllowedInclude = 'location' | 'availableOffers' | 'imagePresets';
export type PlanListAllowedExclude = 'availableOsImageVersions' | 'availableOsImageVersions.osImage' | 'availableApplications' | 'availableLocations';

export interface IPlanListRequest extends IRequest<
    IPlanListFilters
>, IPaginated, ISearchable, IPlanListAdditionalParameters {}
